import { useState } from 'react';
import classNames from 'classnames';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  IncidentObjective,
  useEditIncidentObjective,
} from 'components/IAP/hooks/incidentObjectiveHooks';
import {
  IncidentStrategy,
  useCreateIncidentStrategy,
  useEditIncidentStrategy,
} from 'components/IAP/hooks/incidentStrategyHooks';
import {
  IncidentTactic,
  useCreateIncidentTactic,
  useEditIncidentTactic,
} from 'components/IAP/hooks/incidentTacticHooks';
import { STRATEGIES_LIBRARY, TACTICS_LIBRARY } from '../ostLibraryData';
import { Badge } from '@/components/ui/badge';
import EditableTableCell from './EditableTableCell';
import AddChildRow from './AddChildRow';

type ObjectiveRowsProps = {
  objective: IncidentObjective;
  strategies: IncidentStrategy[];
  tactics: IncidentTactic[];
  readOnly?: boolean;
};

const Td: React.FC<React.ComponentProps<'td'>> = ({ className, ...props }) => (
  <td className={classNames('tw:h-20', className)} {...props} />
);

const CellFooter: React.FC<{
  addForm?: React.ReactNode;
  badges: string[];
  addButton?: React.ReactNode;
}> = ({ addForm, badges, addButton }) => (
  <>
    {addForm}
    <div className="d-flex align-items-center flex-wrap gap-2">
      {badges.map((b) => (
        <Badge key={b} variant="outline" className="tw:text-white">
          {b}
        </Badge>
      ))}
      {addButton}
    </div>
  </>
);

const AddButton: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <StylishNewButton className="button--sml" onClick={onClick}>
    <SharedIcon iconName="add" classes="me-1" size="sm" />
    {label}
  </StylishNewButton>
);

const ObjectiveRows: React.FC<ObjectiveRowsProps> = ({
  objective,
  strategies,
  tactics,
  readOnly = false,
}) => {
  const editObjective = useEditIncidentObjective();
  const createStrategy = useCreateIncidentStrategy();
  const editStrategy = useEditIncidentStrategy();
  const createTactic = useCreateIncidentTactic();
  const editTactic = useEditIncidentTactic();

  const [addingStrategy, setAddingStrategy] = useState(false);
  const [addingTacticFor, setAddingTacticFor] = useState<string | null>(null);

  const objStrategies = strategies.filter(
    (s) => s.objectives.includes(objective.objective_id) && !s.archived
  );
  const strategyTactics = (id: string) =>
    tactics.filter((t) => t.strategies.includes(id) && !t.archived);

  const totalRows =
    objStrategies.length === 0
      ? 1
      : objStrategies.reduce(
          (acc, s) => acc + Math.max(1, strategyTactics(s.strategy_id).length),
          0
        );

  const submitStrategy = (label: string) => {
    createStrategy.mutate({
      name: label,
      description: '',
      tactics: [],
      objectives: [objective.objective_id],
      incident_id: objective.incident_id,
    });
    setAddingStrategy(false);
  };
  const submitTactic = (strat: IncidentStrategy, label: string) => {
    createTactic.mutate({
      name: '',
      description: label,
      strategies: [strat.strategy_id],
      incident_id: strat.incident_id,
    });
    setAddingTacticFor(null);
  };

  const strategyForm = addingStrategy && !readOnly && (
    <AddChildRow
      options={STRATEGIES_LIBRARY}
      placeholder="Select or create Strategy..."
      onCreate={submitStrategy}
      onCancel={() => setAddingStrategy(false)}
    />
  );
  const strategyButton = !readOnly && (
    <AddButton label="Strategy" onClick={() => setAddingStrategy((v) => !v)} />
  );

  const tacticForm = (strat: IncidentStrategy) =>
    addingTacticFor === strat.strategy_id &&
    !readOnly && (
      <AddChildRow
        options={TACTICS_LIBRARY}
        placeholder="Select or create Tactic..."
        onCreate={(label) => submitTactic(strat, label)}
        onCancel={() => setAddingTacticFor(null)}
      />
    );
  const tacticButton = (strat: IncidentStrategy) =>
    !readOnly && (
      <AddButton
        label="Tactic"
        onClick={() =>
          setAddingTacticFor((cur) =>
            cur === strat.strategy_id ? null : strat.strategy_id
          )
        }
      />
    );

  const objectiveCell = (
    <EditableTableCell
      name={objective.name}
      onSave={(name) => editObjective.mutate({ ...objective, name })}
      onDelete={() => editObjective.mutate({ ...objective, archived: true })}
      isDeleteDisabled={objStrategies.length > 0}
      readOnly={readOnly}
      footer={<CellFooter badges={['Objectives', 'Operational']} />}
    />
  );

  if (objStrategies.length === 0) {
    return (
      <tr>
        <Td>{objectiveCell}</Td>
        <Td>
          <div className="d-flex flex-column gap-2">
            <CellFooter
              addForm={strategyForm}
              badges={[]}
              addButton={strategyButton}
            />
          </div>
        </Td>
        <Td />
      </tr>
    );
  }

  const rows: React.ReactNode[] = [];
  let objectiveCellRendered = false;
  objStrategies.forEach((strat, sIdx) => {
    const stratTactics = strategyTactics(strat.strategy_id);
    const strategyRowSpan = Math.max(1, stratTactics.length);
    const isFirstStrategy = sIdx === 0;

    const strategyCell = (
      <EditableTableCell
        name={strat.name}
        onSave={(name) => editStrategy.mutate({ ...strat, name })}
        onDelete={() => editStrategy.mutate({ ...strat, archived: true })}
        isDeleteDisabled={stratTactics.length > 0}
        readOnly={readOnly}
        footer={
          <CellFooter
            addForm={isFirstStrategy ? strategyForm : undefined}
            badges={['Strategies']}
            addButton={isFirstStrategy ? strategyButton : undefined}
          />
        }
      />
    );

    if (stratTactics.length === 0) {
      rows.push(
        <tr key={strat.strategy_id}>
          {!objectiveCellRendered && (
            <Td rowSpan={totalRows}>{objectiveCell}</Td>
          )}
          <Td>{strategyCell}</Td>
          <Td>
            <div className="d-flex flex-column gap-2">
              <CellFooter
                addForm={tacticForm(strat)}
                badges={[]}
                addButton={tacticButton(strat)}
              />
            </div>
          </Td>
        </tr>
      );
      objectiveCellRendered = true;
      return;
    }
    stratTactics.forEach((tac, tIdx) => {
      const isFirstTactic = tIdx === 0;
      rows.push(
        <tr key={`${strat.strategy_id}-${tac.tactic_id}`}>
          {!objectiveCellRendered && (
            <Td rowSpan={totalRows}>{objectiveCell}</Td>
          )}
          {isFirstTactic && <Td rowSpan={strategyRowSpan}>{strategyCell}</Td>}
          <Td>
            <EditableTableCell
              name={tac.description ?? ''}
              onSave={(description) =>
                editTactic.mutate({ ...tac, description })
              }
              onDelete={() => editTactic.mutate({ ...tac, archived: true })}
              readOnly={readOnly}
              footer={
                <CellFooter
                  addForm={isFirstTactic ? tacticForm(strat) : undefined}
                  badges={['Tactics']}
                  addButton={isFirstTactic ? tacticButton(strat) : undefined}
                />
              }
            />
          </Td>
        </tr>
      );
      objectiveCellRendered = true;
    });
  });

  return <>{rows}</>;
};

export default ObjectiveRows;
